import RMModal from 'components/modal/RMModal';
import React, { useEffect, useMemo, useState } from 'react';
import { useRMDispatch } from 'redux/hooks';
import { useCrystals, useCurrentTripCheckedCrystals } from 'redux/hooks/crystal';
import { setHarvestingContentView } from 'redux/reducers/synchrotronTripReducer';
import { IStyle } from 'Styles/styleModel';
import { EHarvestingView } from '../constants';
import { useCrystalCryos, useCrystalSoakings } from '../selectAgents/utils';
import CrystalCardHeader from './CrystalCardHeader';
import CrystalService from 'business/services/CrystalService';
import { useOnCrystalCheckHandler, useSingleCrystalCardClick } from './hooks';
import { CrystalDTO } from 'business/models/Models';
import { CrystalDrawing, DrawingType } from 'components/well/drawingManager/constants';
import { updateDrawing } from 'redux/reducers/wellReducer';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import { useSelectedWellDrawing } from 'redux/hooks/well';
export interface CrystalProps {
  crystal: CrystalDrawing;
  openCrystal: (id: number) => void;
}
const CrystalCard = (props: CrystalProps) => {
  const { crystal, openCrystal } = props;
  const cryoprotectants = useCrystalCryos(crystal);
  const soakings = useCrystalSoakings(crystal);
  const allCrystals = useCrystals();
  const crystalService = new CrystalService();
  const dispatch = useRMDispatch();
  const crystalIds = useCurrentTripCheckedCrystals();
  const onCrystalCardClick = useSingleCrystalCardClick(crystal);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [editNotes, setEditNotes] = useState(false);
  const [crystalNotes, setCrystalNotes] = useState<string>('');
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};
  const selectCrystalOnDrawingSelect = useOnCrystalCheckHandler();
  const selectedDrawing = useSelectedWellDrawing();

  useEffect(() => {
    if (
      selectedDrawing?.drawingType == DrawingType.CRYSTAL &&
      selectedDrawing?.drawingId == crystal.id &&
      !crystal.isChecked
    ) {
      selectCrystalOnDrawingSelect(crystal);
    }
  }, [selectedDrawing]);

  useEffect(() => {
    setCrystalNotes(crystal.notes);
  }, [crystal.notes]);

  const onClickCryoprotectant = (event) => {
    event.stopPropagation();
    if (crystalIds.length > 1) {
      setShowErrorModal(true);
      setErrorMessage(
        'You are not allowed to select cryoprotectants because multiple crystals are selected.',
      );
      return;
    }
    dispatch(setHarvestingContentView(EHarvestingView.SELECTCRYO));
  };

  const onClickSoakings = (event) => {
    event.stopPropagation();
    if (crystalIds.length > 1) {
      setShowErrorModal(true);
      setErrorMessage(
        'You are not allowed to select soakings because multiple crystals are selected.',
      );
      return;
    }
    dispatch(setHarvestingContentView(EHarvestingView.SELECTSOAKING));
  };

  const onSaveNotes = (e) => {
    const tempCrystals = [...allCrystals].map((item) => ({
      ...item,
      notes: item.id === crystal?.id ? e.target.value : item.notes,
    }));
    const tempCrystal = tempCrystals.find((x) => x.id === crystal.id);

    setEditNotes(false);
    dispatch(
      updateDrawing({
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        regionName: regionName,
        drawingType: DrawingType.CRYSTAL,
        id: crystal.id,
        drawingUpdateDetails: { notes: tempCrystal.notes },
      }),
    );

    const crystalDto = CrystalDTO.fromJS(tempCrystal);
    crystalService.updateCrystal(crystalDto);
  };

  const getCryoprotectant = useMemo(() => {
    return (
      <div id={'exp-crsytalcard-cryoprotectant-cotainer'} style={styles.dropdown}>
        <div id={'exp-crsytalcard-cryoprotectant-header'} style={{ width: '30%' }}>
          {' '}
          Cryoprotectant{' '}
        </div>
        <div
          id={'exp-crsytalcard-cryoprotectant-listcotainer'}
          style={styles.listContainer}
        >
          <div
            id={'exp-crsytalcard-cryoprotectant-selectedoptions'}
            style={
              crystal.isChecked
                ? { cursor: 'pointer', ...styles.selectedOptions }
                : { backgroundColor: 'rgb(235,235,228)', ...styles.selectedOptions }
            }
            onClick={(e) => crystal.isChecked && onClickCryoprotectant(e)}
          >
            {cryoprotectants?.length ? (
              cryoprotectants?.map((cryo, index) => (
                <div id={`exp-crsytalcard-soaking-${cryo.label}`} key={index}>
                  {cryo.label}
                </div>
              ))
            ) : (
              <div id={'exp-crsytalcard-cryoprotectant-selected-message'}>
                Select Cryo
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [cryoprotectants, crystal, crystalIds]);

  const getSoaking = useMemo(() => {
    return (
      <div id={'exp-crsytalcard-soaking-cotainer'} style={styles.dropdown}>
        <div id={'exp-crsytalcard-soaking-header'} style={{ width: '30%' }}>
          {' '}
          Soaking{' '}
        </div>
        <div id={'exp-crsytalcard-soaking-listcotainer'} style={styles.listContainer}>
          <div
            id={'exp-crsytalcard-soaking-selectedoptions'}
            style={
              crystal.isChecked
                ? { cursor: 'pointer', ...styles.selectedOptions }
                : { backgroundColor: 'rgb(235,235,228)', ...styles.selectedOptions }
            }
            onClick={(e) => crystal.isChecked && onClickSoakings(e)}
          >
            {soakings?.length ? (
              soakings?.map((soaking, index) => (
                <div id={`exp-crsytalcard-soaking-${soaking.label}`} key={index}>
                  {soaking.label}
                </div>
              ))
            ) : (
              <div id={'exp-crsytalcard-soaking-selectedmessage'}>Select Soaking</div>
            )}
          </div>
        </div>
      </div>
    );
  }, [soakings, crystal, crystalIds]);

  const getNotes = useMemo(() => {
    return (
      <div id={'exp-crsytalcard-notes-container'} style={styles.dropdown}>
        <div id={'exp-crsytalcard-notes-header'} style={{ width: '30%' }}>
          {' '}
          Notes{' '}
        </div>
        <div id={'exp-crsytalcard-notes-listcontainer'} style={styles.listContainer}>
          <div
            id={'exp-crsytalcard-notes-selectedoptions'}
            onClick={() => setEditNotes(true)}
            style={
              crystal.isChecked
                ? { cursor: 'pointer', ...styles.selectedOptions }
                : { backgroundColor: 'rgb(235,235,228)', ...styles.selectedOptions }
            }
          >
            {!editNotes ? (
              <div id={'exp-crsytalcard-notes-edit'}>
                {crystal.notes ? crystal.notes : 'Click to edit'}
              </div>
            ) : (
              <div id={'exp-crsytalcard-notes-input'}>
                <input
                  id={'exp-crsytalcard-notes-input-text'}
                  type="text"
                  value={crystalNotes}
                  onChange={(e) => setCrystalNotes(e.target.value)}
                  style={styles.notesInput}
                  onBlur={onSaveNotes}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [editNotes, crystal, crystalNotes]);

  return (
    <>
      <div
        id={'exp-crsytalcard-container'}
        onClick={onCrystalCardClick}
        style={
          crystal.isChecked
            ? { backgroundColor: 'rgb(133, 186, 255)', ...styles.cardRoot }
            : styles.cardRoot
        }
      >
        <div id={'exp-crsytalcard-header'} style={styles.cardHeader}>
          <CrystalCardHeader crystal={crystal} openCrystal={openCrystal} />
        </div>
        <div id={'exp-crsytalcard-listcontainer'} style={styles.cardDrowpdown}>
          {getCryoprotectant}
          {getSoaking}
          {getNotes}
        </div>
      </div>
      <RMModal
        id={'exp-crsytalcard-info'}
        showModal={showErrorModal}
        setShowModal={setShowErrorModal}
        onOk={() => setShowErrorModal(false)}
        body={errorMessage}
        heading={'Info'}
        isOkOnly={true}
        okButtonText={'Ok'}
      />
    </>
  );
};
export default CrystalCard;
const styles: IStyle = {
  cardHeader: {
    display: 'flex',
  },
  cardRoot: {
    outline: '2px solid #CCC',
    border: '1px solid #999',
    borderRadius: '5px',
    paddingTop: '2%',
    paddingBottom: '1%',
    padding: '0.5%',
    marginTop: '1%',
  },
  dropdown: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '1%',
  },
  listContainer: {
    position: 'relative',
    width: '75%',
  },
  selectedOptions: {
    padding: '1%',
    border: '1px solid gray',
  },
  notesInput: {
    width: '100%',
  },
};
